/**
 * PHA-1212 / PHA-1213: what the Care Plans tab says about the care plan's EHR upload.
 *
 * Uploading is asynchronous. Cobalt answers the POST with "accepted", and only the
 * document.uploaded / document.upload_failed webhook — normally within a minute — says
 * whether the EHR actually filed it. So the tab shows one of three states:
 *
 *   Pending  — Upload submitted, awaiting confirmation.     (push row 'pending' | 'accepted')
 *   Uploaded — Successfully added to the EHR.               (push row 'filed' | 'superseded')
 *   Failed   — Upload unsuccessful; retry available.        (push row 'failed')
 *
 * Only a push of the CURRENT approved plan version is described. The status endpoint
 * returns the patient's latest push, which after a regeneration in a clinic without
 * automatic upload is the PREVIOUS plan's; calling that "Uploaded" would tell a clinician
 * the plan on screen is in the chart when it is not.
 */

export type CarePlanEhrLastPush = {
  push_id?: string | null;
  status: string;
  care_plan_version?: string | number | null;
  requested_at?: string | null;
  confirmed_at?: string | null;
  error?: string | null;
  pushed_by_name?: string | null;
  automatic?: boolean;
  /** PHA-1223: whether Phanova's scheduler will re-file this failed push by itself. */
  auto_retry?: {
    transient: boolean;
    attempts: number;
    max_attempts: number;
    scheduled: boolean;
    next_at?: string | null;
    exhausted: boolean;
  } | null;
};

export type CarePlanEhrPushStatus = {
  push_enabled: boolean;
  auto_push_enabled?: boolean;
  already_sent: boolean;
  folder_name?: string | null;
  care_plan_version?: string | number | null;
  last_push?: CarePlanEhrLastPush | null;
};

export type CarePlanEhrPushState = 'none' | 'pending' | 'uploaded' | 'failed';

/**
 * A push still unconfirmed after this long is not "about to land": the webhook normally
 * arrives within a minute. Past it the tab stops polling and says no confirmation came,
 * rather than promising one indefinitely (staging never receives Cobalt webhooks at all).
 */
export const PENDING_CONFIRMATION_WINDOW_MS = 5 * 60 * 1000;

export const EHR_PUSH_STATE_LABEL: Record<Exclude<CarePlanEhrPushState, 'none'>, string> = {
  pending: 'Pending',
  uploaded: 'Uploaded',
  failed: 'Failed',
};

export const EHR_PUSH_PENDING_DETAIL = 'Upload submitted, awaiting confirmation.';
export const EHR_PUSH_UPLOADED_DETAIL = 'Successfully added to the EHR';

/** PHA-1213: shown when the clinician stops waiting (or we do) before the EHR answers. */
export const EHR_PUSH_SUBMITTED_DETAIL =
  'The care plan has been submitted to the EHR. The final upload status will appear in the ' +
  "patient's Care Plans tab within approximately one minute.";

export const carePlanEhrPushStateOf = (push?: CarePlanEhrLastPush | null): CarePlanEhrPushState => {
  switch (push?.status) {
    case 'pending':
    case 'accepted':
      return 'pending';
    // 'superseded' is a push the EHR filed that a later copy of the same version replaced.
    case 'filed':
    case 'superseded':
      return 'uploaded';
    case 'failed':
      return 'failed';
    default:
      return 'none';
  }
};

/** The latest push, if it is a push of the plan currently approved; otherwise null. */
export const currentVersionPush = (
  status?: CarePlanEhrPushStatus | null,
): CarePlanEhrLastPush | null => {
  const push = status?.last_push;
  if (!push) return null;
  const current = status?.care_plan_version;
  if (current === null || current === undefined || current === '') return null;
  if (push.care_plan_version === null || push.care_plan_version === undefined) return null;
  return String(push.care_plan_version) === String(current) ? push : null;
};

export type CarePlanEhrPushDisplay = {
  state: CarePlanEhrPushState;
  push: CarePlanEhrLastPush | null;
  /** Pending for longer than PENDING_CONFIRMATION_WINDOW_MS. */
  stale: boolean;
};

export const describeCarePlanEhrPush = (
  status?: CarePlanEhrPushStatus | null,
  now: number = Date.now(),
): CarePlanEhrPushDisplay => {
  const push = currentVersionPush(status);
  const state = carePlanEhrPushStateOf(push);
  if (!push || state === 'none') return { state: 'none', push: null, stale: false };
  let stale = false;
  if (state === 'pending') {
    const requested = push.requested_at ? Date.parse(push.requested_at) : NaN;
    stale = Number.isFinite(requested) && now - requested > PENDING_CONFIRMATION_WINDOW_MS;
  }
  return { state, push, stale };
};

/**
 * PHA-1223: what a clinician is told about a failed upload.
 *
 * The raw error is Cobalt's wording, written for an integrator: "Document upload failed after
 * 4 attempts. Please contact support." sent ODA staff looking for someone to call about an EHR
 * hiccup that fixed itself an hour later. Known errors are put in the clinician's terms, the
 * "contact support" sentence is dropped (Phanova is alerted on every failure), and the second
 * sentence says what happens next — including that Phanova will retry, but only when the
 * server says a retry is actually scheduled.
 */
export type EhrPushFailureCopy = {
  /** What went wrong, in one sentence. */
  reason: string;
  /** What happens next / what to do. */
  next: string;
  /** True when no one has to do anything — Phanova will retry on its own. */
  autoRetryScheduled: boolean;
};

const CONTACT_SUPPORT = /\s*please contact (?:cobalt )?support\.?/gi;

export const isTemporaryEhrPushError = (error?: string | null): boolean => {
  const text = (error || '').toLowerCase();
  if (!text) return false;
  if (/patient not found|folder not found|mrn|invalid|unauthori[sz]ed|forbidden/.test(text)) return false;
  return /failed after \d+ attempts|\((?:429|500|502|503|504)\)|timed? ?out|timeout|temporar|service unavailable|bad gateway|too many requests|connection (?:error|reset|aborted|refused)|max retries exceeded/.test(
    text,
  );
};

export const describeEhrPushFailure = (push?: CarePlanEhrLastPush | null): EhrPushFailureCopy => {
  const raw = (push?.error || '').trim();
  const retry = push?.auto_retry || null;
  const temporary = retry ? retry.transient : isTemporaryEhrPushError(raw);
  const lower = raw.toLowerCase();

  let reason: string;
  let next: string;
  if (temporary) {
    reason = 'The EHR was temporarily not accepting documents';
    if (retry?.scheduled) {
      const attempt = Math.min((retry.attempts || 0) + 1, retry.max_attempts || 3);
      next =
        `Phanova will retry the upload automatically within about 30 minutes ` +
        `(attempt ${attempt} of ${retry.max_attempts || 3}) — no action needed. You can also retry now.`;
    } else if (retry?.exhausted) {
      next =
        'Phanova retried automatically without success and has been alerted. ' +
        'You can retry now, or wait for the Phanova team to follow up.';
    } else {
      next = 'This is usually temporary — please retry in a few minutes.';
    }
  } else if (lower.includes('patient not found')) {
    reason = "The EHR could not find this patient's MRN";
    next = 'Check that the MRN in Phanova matches the EHR, then retry. The Phanova team has been alerted.';
  } else if (lower.includes('folder not found')) {
    reason = "The EHR folder configured for this clinic's care plans was not found";
    next = 'The Phanova team has been alerted and will fix the clinic setting; retry once it is fixed.';
  } else {
    const cleaned = raw.replace(CONTACT_SUPPORT, '').trim().replace(/\.\s*$/, '');
    reason = cleaned ? `The EHR did not accept the care plan: ${cleaned}` : 'The EHR did not accept the care plan';
    next = 'The Phanova team has been alerted. You can retry now.';
  }
  return { reason, next, autoRetryScheduled: Boolean(temporary && retry?.scheduled) };
};

/** True while the tab should keep re-reading the status to pick up the webhook's verdict. */
export const isAwaitingEhrConfirmation = (
  status?: CarePlanEhrPushStatus | null,
  now: number = Date.now(),
): boolean => {
  const display = describeCarePlanEhrPush(status, now);
  return display.state === 'pending' && !display.stale;
};
